import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { UsersApi } from '@/api/clients/users/users.api';
import { useGetFacilityUser, useUpdateFacilityUser } from '@/api/hooks/users';
import { API_BASE_URL } from '@/config';
import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';

import { buildFacilityUserScopePayload } from '../../UserManagement/components/facilityUserScope.utils';
import {
  buildBasicInfoFormData,
  buildEmployeeInfoFormData,
  buildPermissionsFormData,
  wasFacilityGroupScoped,
} from '../userDetailsFormData';

export const useUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const basicInfoRef = useRef(null);
  const employeeInfoRef = useRef(null);
  const previousRoleBasedFacilitiesAccessRef = useRef(false);

  const { user, loading, error: fetchError, refetch } = useGetFacilityUser({
    id: userId,
  });
  const {
    updateUser,
    loading: updateLoading,
    error: updateError,
  } = useUpdateFacilityUser();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  });

  const [basicInfo, setBasicInfo] = useState({});
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [permissionsType, setPermissionsType] = useState('permission');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [accessMode, setAccessMode] = useState(FACILITY_USER_ACCESS_MODE.NONE);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedFacilityGroupIds, setSelectedFacilityGroupIds] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [employeeInfo, setEmployeeInfo] = useState({});
  const [facilities, setFacilities] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [permissionsTemplate, setPermissionsTemplate] = useState([]);
  const [roleBasedFacilitiesAccess, setRoleBasedFacilitiesAccess] = useState(
    false,
  );
  const [guardError, setGuardError] = useState(null);

  useEffect(() => {
    if (!user) {
      return;
    }

    const nextAccessMode =
      user.user_data?.access_mode || FACILITY_USER_ACCESS_MODE.NONE;

    setBasicInfo({
      email: user.user_data?.email || '',
      userName: user.user_data?.name || '',
      name: user.user_data?.name || '',
      mfa_type: user.user_data?.mfa_type || 'sms',
      languagePreference: user.user_data?.language_preference || 'en',
    });

    setAccessMode(nextAccessMode);

    const facilitiesFromBackend =
      user.facilities_associated?.filter((facility) => facility.id !== null) ||
      [];
    setSelectedFacilities(facilitiesFromBackend.map((facility) => facility.id));
    setSelectedFacilityGroupIds(
      (user.assigned_facility_groups || []).map((group) => group.id),
    );

    if (user.user_data?.organization_id) {
      setSelectedOrganization(`${user.user_data.organization_id}`);
    }

    if (
      user.user_data?.permissions_type === 'role' &&
      user.user_data?.role_id
    ) {
      setPermissionsType('role');
      setSelectedRoleId(user.user_data.role_id);
      setFacilityPermissions({});
      setOrganizationPermissions({});
    } else {
      setPermissionsType('permission');
      setSelectedRoleId(null);

      const facilityPerms = {};
      user.facility_and_permissions?.forEach((fp) => {
        facilityPerms[`${fp.facilityId}`] = fp.permissions;
      });
      setFacilityPermissions(facilityPerms);

      const orgPerms = {};
      if (user.organization_permissions?.permissions) {
        const orgId = String(user.organization_permissions.organizationId);
        orgPerms[orgId] = user.organization_permissions.permissions;
        setSelectedOrganization(orgId);
      } else if (user.user_data?.organization_id) {
        const orgId = String(user.user_data.organization_id);
        orgPerms[orgId] = {};
      }
      setOrganizationPermissions(orgPerms);
    }

    setGuardError(null);
    previousRoleBasedFacilitiesAccessRef.current = false;
    setRoleBasedFacilitiesAccess(false);
  }, [user]);

  useEffect(() => {
    const previousRoleBasedFacilitiesAccess =
      previousRoleBasedFacilitiesAccessRef.current;

    if (previousRoleBasedFacilitiesAccess === roleBasedFacilitiesAccess) {
      return;
    }

    if (roleBasedFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.ALL_FACILITIES);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
      setGuardError(null);
    } else if (previousRoleBasedFacilitiesAccess) {
      setAccessMode(FACILITY_USER_ACCESS_MODE.NONE);
      setSelectedFacilities([]);
      setSelectedFacilityGroupIds([]);
    }

    previousRoleBasedFacilitiesAccessRef.current = roleBasedFacilitiesAccess;
  }, [roleBasedFacilitiesAccess]);

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const facilitiesResponse = await axios.get(
          `${API_BASE_URL}/facilitiesPro`,
        );
        setFacilities(facilitiesResponse.data);

        const organizationsResponse = await OrganizationsApi.getOrganizations();
        setOrganizations(organizationsResponse.data.organizations);

        const permissionsResponse = await axios.get(
          `${API_BASE_URL}/permissions`,
        );
        setPermissionsTemplate(permissionsResponse.data);
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };

    fetchReferenceData();
  }, []);

  const handleBasicInfoChange = useCallback((newBasicInfo) => {
    setBasicInfo(newBasicInfo);
  }, []);

  const handlePermissionChange = useCallback(
    (newPermissions, targetId, permissionLevel) => {
      if (permissionLevel === 'facility') {
        setFacilityPermissions((prev) => ({
          ...prev,
          [targetId]: newPermissions,
        }));
      } else {
        setOrganizationPermissions((prev) => ({
          ...prev,
          [targetId]: newPermissions,
        }));
      }
    },
    [],
  );

  const handlePermissionsTypeChange = useCallback(
    (newType) => {
      setGuardError(null);
      setPermissionsType(newType);

      if (newType === 'role') {
        setFacilityPermissions({});
        setOrganizationPermissions({});
        return;
      }

      setSelectedRoleId(null);
      setRoleBasedFacilitiesAccess(false);

      if (accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS) {
        setAccessMode(FACILITY_USER_ACCESS_MODE.NONE);
        setSelectedFacilities([]);
        setSelectedFacilityGroupIds([]);
      }

      if (!user) {
        return;
      }

      const facilityPerms = {};
      user.facility_and_permissions?.forEach((fp) => {
        facilityPerms[`${fp.facilityId}`] = fp.permissions;
      });
      setFacilityPermissions(facilityPerms);

      const orgPerms = {};
      if (user.organization_permissions?.permissions) {
        const orgId = String(user.organization_permissions.organizationId);
        orgPerms[orgId] = user.organization_permissions.permissions;
      } else if (selectedOrganization) {
        orgPerms[selectedOrganization] = {};
      } else if (user.user_data?.organization_id) {
        const orgId = String(user.user_data.organization_id);
        orgPerms[orgId] = {};
      }
      setOrganizationPermissions(orgPerms);
    },
    [accessMode, selectedOrganization, user],
  );

  const handleRoleChange = useCallback(
    (roleId, hasRoleBasedFacilitiesAccess) => {
      setGuardError(null);
      setSelectedRoleId(roleId);
      setRoleBasedFacilitiesAccess(hasRoleBasedFacilitiesAccess);
    },
    [],
  );

  const handleOrganizationChange = useCallback(
    (organizationId) => {
      const oldOrgId = selectedOrganization;
      setSelectedOrganization(organizationId);

      if (organizationId !== oldOrgId) {
        const newOrgPermissions = {};
        if (organizationId) {
          newOrgPermissions[organizationId] = {};
        }
        setOrganizationPermissions(newOrgPermissions);
      }
    },
    [selectedOrganization],
  );

  const handleAccessModeChange = useCallback((newMode) => {
    setGuardError(null);
    setAccessMode(newMode);

    if (newMode !== FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES) {
      setSelectedFacilities([]);
    }

    if (newMode !== FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS) {
      setSelectedFacilityGroupIds([]);
    }
  }, []);

  const handleTabChange = useCallback(
    (_event, newValue) => {
      setActiveTab(newValue);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newValue === 0) {
        newSearchParams.delete('tab');
      } else {
        newSearchParams.set('tab', newValue.toString());
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const saveFacilityUserScope = async () => {
    await UsersApi.updateFacilityUserScope(
      userId,
      buildFacilityUserScopePayload({
        accessMode,
        facilityIds: selectedFacilities,
        facilityGroupIds: selectedFacilityGroupIds,
      }),
    );
  };

  const buildFormDataForActiveTab = () => {
    if (activeTab === 0) {
      if (!basicInfoRef.current || !basicInfoRef.current.validateForm()) {
        return null;
      }

      return {
        formData: buildBasicInfoFormData({
          basicInfo,
          permissionsType,
          selectedRoleId,
          accessMode,
          selectedFacilities,
          selectedOrganization,
          user,
        }),
        successMsg: 'Basic information updated successfully',
      };
    }

    if (activeTab === 1 || activeTab === 2) {
      return {
        formData: buildPermissionsFormData({
          user,
          permissionsType,
          selectedRoleId,
          selectedFacilities,
          selectedOrganization,
          facilityPermissions,
          organizationPermissions,
        }),
        successMsg: 'Permissions updated successfully',
      };
    }

    if (activeTab === 3) {
      return {
        formData: buildEmployeeInfoFormData(employeeInfo),
        successMsg: 'Employee information updated successfully',
      };
    }

    return null;
  };

  const handleSave = async () => {
    try {
      const saveState = buildFormDataForActiveTab();

      if (!saveState) {
        return;
      }

      const { formData, successMsg } = saveState;
      const shouldClearFacilityGroupScopeBeforeUserUpdate =
        activeTab === 0 &&
        permissionsType !== 'role' &&
        wasFacilityGroupScoped(user);

      if (shouldClearFacilityGroupScopeBeforeUserUpdate) {
        try {
          await saveFacilityUserScope();
        } catch (scopeError) {
          console.error('Error saving facility user scope:', scopeError);
          setGuardError(
            'Saving facility access failed. Review the user scope and save again.',
          );
          await refetch();
          return;
        }
      }

      await updateUser(userId, formData);

      if (activeTab === 0 && permissionsType === 'role') {
        try {
          await saveFacilityUserScope();
        } catch (scopeError) {
          console.error('Error saving facility user scope:', scopeError);
          setSuccessMessage(
            'Basic information updated, but saving facility access failed. Review the user scope and save again.',
          );
          setShowSuccessAlert(true);
          await refetch();
          return;
        }
      }

      await refetch();

      setSuccessMessage(successMsg);
      setShowSuccessAlert(true);

      setTimeout(() => {
        const successAlert = document.getElementById('success-alert');
        if (successAlert) {
          successAlert.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleBack = useCallback(() => {
    navigate('/admin/usermanagement');
  }, [navigate]);

  const handleSuccessClose = useCallback(() => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  }, []);

  const handleResendActivation = async () => {
    try {
      setResendLoading(true);
      setResendError(null);
      await axios.post(`${API_BASE_URL}/admin/user/resend_activation_email`, {
        email: user.user_data.email,
        username: user.user_data.email,
        name: user.user_data.name,
      });

      setSuccessMessage('Activation email sent successfully');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (err) {
      console.error('Error resending activation:', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to send activation email';
      setResendError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const hasFacilityAccess =
    (accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS &&
      selectedFacilityGroupIds.length > 0) ||
    accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES ||
    (accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES &&
      selectedFacilities.length > 0);

  const isFormDisabled =
    updateLoading ||
    resendLoading ||
    (activeTab === 0 &&
      (!basicInfoRef.current?.isFormValid || !hasFacilityAccess));

  return {
    userId,
    user,
    loading,
    fetchError,
    updateLoading,
    updateError,
    showSuccessAlert,
    successMessage,
    resendLoading,
    resendError,
    activeTab,
    basicInfoRef,
    employeeInfoRef,
    facilityPermissions,
    organizationPermissions,
    permissionsType,
    selectedRoleId,
    accessMode,
    selectedFacilities,
    selectedFacilityGroupIds,
    selectedOrganization,
    facilities,
    organizations,
    permissionsTemplate,
    roleBasedFacilitiesAccess,
    guardError,
    isFormDisabled,
    handleBack,
    handleSuccessClose,
    handleBasicInfoChange,
    handlePermissionChange,
    handlePermissionsTypeChange,
    handleRoleChange,
    handleOrganizationChange,
    handleAccessModeChange,
    handleTabChange,
    handleSave,
    handleResendActivation,
    setSelectedFacilities,
    setSelectedFacilityGroupIds,
    setRoleBasedFacilitiesAccess,
    setEmployeeInfo,
  };
};
